import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, Edit, UserX, UserCheck, Plus, Trash2 } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Avatar from '../../components/common/Avatar';
import Badge from '../../components/common/Badge';
import Select from '../../components/common/Select';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import AddStoreLinkModal from '../../components/admin/AddStoreLinkModal';
import { adminService } from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';
import { formatPrice } from '../../utils/priceCalculator';
import { formatOrderNumber } from '../../utils/statusBucket';
import { formatRole, formatBusinessType } from '../../utils/enums';
import UserFormModal from './UserFormModal';

const formatDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('pt-BR');
  } catch { return s; }
};

const ROLE_IN_STORE_OPTIONS = [
  { value: 'client', label: formatRole('client') },
  { value: 'reseller', label: formatRole('reseller') },
  { value: 'staff', label: formatRole('staff') },
  { value: 'owner', label: formatRole('owner') }
];

const AdminUserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error, success } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [unlinking, setUnlinking] = useState(null);
  const [savingRoleFor, setSavingRoleFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await adminService.getUser(id);
      setData(resp);
    } catch (e) { error(e.message); }
    finally { setLoading(false); }
  }, [id, error]);

  useEffect(() => { load(); }, [load]);

  const runReset = async () => {
    try {
      await adminService.resetUserPassword(id);
      success('Email de reset enviado');
    } catch (e) { error(e.message); }
  };

  const toggleStatus = async () => {
    if (!data?.user) return;
    const u = data.user;
    const willSuspend = u.status === 'active';
    try {
      await adminService.updateUserStatus(u.id, {
        status: willSuspend ? 'suspended' : 'active',
        active: !willSuspend
      });
      success(willSuspend ? 'Usuário suspenso' : 'Usuário reativado');
      load();
    } catch (e) { error(e.message); }
  };

  const handleChangeRole = async (storeId, nextRole) => {
    setSavingRoleFor(storeId);
    try {
      await adminService.updateUserStoreRole(id, storeId, { roleInStore: nextRole });
      success('Papel atualizado');
      load();
    } catch (e) {
      error(e.message);
    } finally {
      setSavingRoleFor(null);
    }
  };

  const handleUnlink = async () => {
    if (!unlinking) return;
    try {
      await adminService.unlinkUserFromStore(id, unlinking.storeId);
      success('Vínculo removido');
      load();
    } catch (e) {
      error(e.message);
    } finally {
      setUnlinking(null);
    }
  };

  if (loading) return <div className="p-8 text-app-text-secondary">Carregando…</div>;
  if (!data) return <EmptyState title="Usuário não encontrado" />;

  const user = data.user;
  const memberships = data.memberships || [];
  const orders = data.orders || [];
  const linkedStoreIds = memberships.map((m) => m.storeId);

  return (
    <div className="max-w-5xl mx-auto">
      <Button variant="ghost" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate(-1)} className="mb-4">
        Voltar
      </Button>

      <Card className="p-5 mb-4">
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <Avatar name={user.name} size="lg" />
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-app-text truncate">{user.name}</h1>
              <p className="text-sm text-app-text-secondary">{user.email}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge color="neutral" variant="soft" size="sm">{formatRole(user.role)}</Badge>
                <StatusBadge domain="user" status={user.status} />
                {!user.active && <Badge color="error" size="sm" variant="soft">Inativo</Badge>}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="subtle" leftIcon={<Edit size={16} />} onClick={() => setEditOpen(true)}>Editar</Button>
            <Button variant="subtle" leftIcon={<KeyRound size={16} />} onClick={() => setConfirm({ kind: 'reset' })}>
              Reset de senha
            </Button>
            {user.status === 'active' ? (
              <Button variant="danger" leftIcon={<UserX size={16} />} onClick={() => setConfirm({ kind: 'suspend' })}>
                Suspender
              </Button>
            ) : (
              <Button variant="primary" leftIcon={<UserCheck size={16} />} onClick={() => setConfirm({ kind: 'activate' })}>
                Reativar
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
          <Field label="CPF" value={user.cpf || '—'} />
          <Field label="Telefone" value={user.phone || '—'} />
          <Field label="Idioma" value={user.preferredLanguage} />
          <Field label="Cadastro" value={formatDate(user.createdAt)} />
        </div>
      </Card>

      <Card className="p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Vínculos com lojas</h2>
          <Button size="sm" variant="primary" leftIcon={<Plus size={14} />} onClick={() => setLinkModalOpen(true)}>
            Adicionar vínculo
          </Button>
        </div>
        {memberships.length === 0 ? (
          <p className="text-sm text-app-text-secondary">Sem vínculos. Use o botão acima pra ligar este usuário a uma loja.</p>
        ) : (
          <div className="space-y-2">
            {memberships.map((m) => (
              <div key={`${m.storeId}-${m.roleInStore}`} className="flex items-center justify-between p-3 rounded bg-app-surface-2 gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-medium">
                    {m.storeName} <span className="text-app-text-secondary text-sm">/{m.slug}</span>
                  </p>
                  <p className="text-xs text-app-text-secondary">
                    Tipo: {formatBusinessType(m.businessType) || '—'} · Vinculado em {formatDate(m.createdAt)}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select
                    value={m.roleInStore}
                    onChange={(e) => handleChangeRole(m.storeId, e.target.value)}
                    options={ROLE_IN_STORE_OPTIONS}
                    disabled={savingRoleFor === m.storeId}
                  />
                  <StatusBadge domain="user" status={m.status} />
                  <Button
                    size="sm"
                    variant="danger"
                    leftIcon={<Trash2 size={14} />}
                    onClick={() => setUnlinking({ storeId: m.storeId, storeName: m.storeName })}
                  >
                    Remover
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="text-lg font-semibold mb-3">Histórico de pedidos (últimos 25)</h2>
        {orders.length === 0 ? (
          <p className="text-sm text-app-text-secondary">Sem pedidos como cliente.</p>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between p-3 rounded bg-app-surface-2 text-sm">
                <div>
                  <p className="font-mono">{formatOrderNumber(o)}</p>
                  <p className="text-xs text-app-text-secondary">{formatDate(o.createdAt)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold">{formatPrice(o.totalPrice || 0)}</span>
                  <StatusBadge domain="order" status={o.statusBucket || o.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <UserFormModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        user={user}
        onSaved={load}
      />

      <AddStoreLinkModal
        isOpen={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        userId={user.id}
        existingStoreIds={linkedStoreIds}
        onSaved={load}
      />

      <ConfirmDialog
        isOpen={!!unlinking}
        onClose={() => setUnlinking(null)}
        onConfirm={handleUnlink}
        title="Remover vínculo?"
        message={`O usuário deixará de ter acesso a "${unlinking?.storeName || 'esta loja'}".`}
        confirmLabel="Remover"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          if (confirm?.kind === 'reset') await runReset();
          else await toggleStatus();
          setConfirm(null);
        }}
        title={
          confirm?.kind === 'reset' ? 'Resetar senha?' :
          confirm?.kind === 'suspend' ? 'Suspender usuário?' : 'Reativar usuário?'
        }
        message={
          confirm?.kind === 'reset' ? 'Um email com link de redefinição será enviado para o usuário.' :
          confirm?.kind === 'suspend' ? 'O usuário não conseguirá mais fazer login até ser reativado.' :
          'O usuário voltará a poder acessar o sistema.'
        }
        confirmLabel={confirm?.kind === 'suspend' ? 'Suspender' : 'Confirmar'}
        variant={confirm?.kind === 'suspend' ? 'danger' : 'primary'}
      />
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-app-text-secondary uppercase">{label}</p>
    <p className="font-medium text-app-text break-words">{value}</p>
  </div>
);

export default AdminUserDetail;
